import colorUtils from '../../util/color';
import { Editor, type Path } from 'slate';
import TableUtils from '../../plugins/TablePlugin/TableUtils';
import { getTableStructureFromElement } from '../../plugins/TablePlugin/TableStructure';
import { isTableRowElement } from '../../types/guards';

export const hasTableHeaderRow = (el: HTMLTableElement) => {
  const rowCellsAreHeaders = (row?: Element | null) => {
    return (
      row &&
      row.tagName.toLowerCase() === 'tr' &&
      Array.from(row.children).every((el) => el.tagName.toLowerCase() === 'th')
    );
  };

  const children = Array.from(el.children);

  const thead = children.find((el) => el.tagName.toLowerCase() === 'thead');
  if (thead) {
    return rowCellsAreHeaders(thead.firstElementChild);
  }
  const tbody = children.find((el) => el.tagName.toLowerCase() === 'tbody');
  if (tbody) {
    return rowCellsAreHeaders(tbody.firstElementChild);
  }

  return rowCellsAreHeaders(
    children.find((el) => el.tagName.toLowerCase() === 'tr')
  );
};

export const hasTableHeaderColumn = (el: HTMLTableElement) => {
  if (el.children.length === 0) return false;

  const firstCellsAreHeaders = (rows: HTMLCollection) => {
    return Array.from(rows).every(
      (row) => row.firstElementChild?.tagName.toLowerCase() === 'th'
    );
  };

  if (el.firstElementChild?.tagName.toLowerCase() === 'tr') {
    return firstCellsAreHeaders(el.children);
  } else {
    const tbody = Array.from(el.children).find(
      (el) => el.tagName.toLowerCase() === 'tbody'
    ) as HTMLTableSectionElement | undefined;
    if (!tbody) return false;

    return firstCellsAreHeaders(tbody.children);
  }
};

export const isHeaderCell = (editor: Editor, path: Path) => {
  const table = TableUtils.parentTable(editor, path);
  const hasHeaderRow = table?.[0].table_headerRow;
  const hasHeaderColumn = table?.[0].table_headerColumn;

  if (hasHeaderRow) {
    const rowPath = path.slice(0, -1);
    if (rowPath.slice(-1)[0] === 0) {
      const tableRow = Editor.node(editor, rowPath);
      if (tableRow && isTableRowElement(tableRow[0])) {
        return true;
      }
    }
  }

  if (hasHeaderColumn) {
    const columnPath = path.slice(-1);
    if (columnPath[0] === 0) {
      return true;
    }
  }

  return false;
};

export const getWidthOfCell = (editor: Editor, cellPath: Path) => {
  const table = TableUtils.parentTable(editor, cellPath);
  if (!table) return;

  const tableStructure = getTableStructureFromElement(editor, table[0]);
  if (!tableStructure) return;

  const coords = tableStructure.pathToCoordinates(cellPath.slice(-2));
  if (!coords) return;

  const cellInfo = tableStructure.cell(coords.columnIndex, coords.rowIndex);
  if (!cellInfo) return;

  const start = coords.columnIndex;
  const end = start + Number(cellInfo.tableCell.table_colspan ?? 1);

  const columnWidths: number[] = table[0].table_columnWidths ?? [];
  if (columnWidths.length === 0) return;

  return columnWidths
    .slice(start, end)
    .reduce((totalWidth, width) => totalWidth + width, 0);
};

export const isTableWrapper = (el: HTMLElement): el is HTMLDivElement => {
  if (el.tagName.toLowerCase() !== 'div') return false;

  const { children } = el;

  const tables = Array.from(children).filter(
    (el) => el.tagName.toLowerCase() === 'table'
  );

  // Table wrappers are a div that has 1 table child and a maxWidth of 100%
  return tables.length === 1 && el.style.maxWidth === '100%';
};

export const isTableRow = (el: HTMLElement): el is HTMLTableRowElement => {
  return el.tagName.toLowerCase() === 'tr';
};

export const isTableCell = (el: HTMLElement): el is HTMLTableCellElement => {
  return ['th', 'td'].includes(el.tagName.toLowerCase());
};

export const getRowspan = (el: HTMLElement) => {
  const rowSpan = parseInt(el.getAttribute('rowspan') ?? '', 10);
  if (!isNaN(rowSpan)) return rowSpan;
};

export const getColspan = (el: HTMLElement) => {
  const colSpan = parseInt(el.getAttribute('colspan') ?? '', 10);
  if (!isNaN(colSpan)) return colSpan;
};

export const hasColspan = (el: HTMLElement) => {
  const colSpan = getColspan(el);
  return Boolean(colSpan && colSpan > 1);
};

export const getBackgroundColor = (el: HTMLElement) => {
  if (el.style.backgroundColor && !el.style.backgroundColor.startsWith('var')) {
    return colorUtils.rgbToHex(el.style.backgroundColor);
  }
};

export const hasVerticalAlign = (el: HTMLElement) =>
  el.style.verticalAlign || el.getAttribute('valign');

// We only support top, middle, bottom alignment
const normalizeVerticalAlignValue = (parsedValue: string) => {
  let normalizedValue = String(parsedValue).toLowerCase();
  if (['baseline', 'sub'].includes(normalizedValue)) {
    normalizedValue = 'bottom';
  }
  return ['top', 'middle', 'bottom'].find((value) => value === normalizedValue);
};

export const getVerticalAlign = (el: HTMLElement) => {
  let verticalAlign = '';

  // style rule has precedence
  if (el.style.verticalAlign) {
    // baseline, top, middle, bottom (also, baseline, sub)
    verticalAlign = el.style.verticalAlign;
  } else if (el.getAttribute('valign')) {
    // baseline, top, middle, bottom
    verticalAlign = el.getAttribute('valign') ?? '';
  }

  return normalizeVerticalAlignValue(verticalAlign);
};

export const parseColumnWidths = (
  columns: HTMLTableColElement[] | HTMLTableCellElement[]
) => {
  // It's dangerous to parse column widths from columns with colspan,
  // because we don't necessarily know the correct width for each
  // column -- only the combined width for the colspan.
  if (columns.some((column) => hasColspan(column))) {
    return [];
  }

  return columns.map((el) => {
    const widthStyle = (el.style.width || el.getAttribute('width')) ?? '';
    const width = parseFloat(widthStyle);
    return isNaN(width) ? 0 : width;
  });
};

// `col` tags can have a `span` attribute that causes the tag to act
// like there are `span` copies of that tag. In order to simplify code
// downstream, this function will return `span` copies of each `col`
// element in the group.
export const expandColsInColGroup = (colElements: HTMLTableColElement[]) => {
  return colElements.reduce<HTMLTableColElement[]>((expandedElements, col) => {
    const attr = col.getAttribute('span') ?? '';
    const span = /^\d+$/.test(attr) ? parseInt(attr, 10) : 1;

    for (let i = 0; i < span; i++) {
      const node = col.cloneNode(false) as HTMLTableColElement;
      node.removeAttribute('span');
      expandedElements.push(node);
    }

    return expandedElements;
  }, []);
};

export const columnWidthsFromTable = (table: HTMLTableElement) => {
  const children = Array.from(table.children);

  const colgroup = children.find(
    (child) => child.tagName.toLowerCase() === 'colgroup'
  );

  // If we have a colgroup, use the widths from it.
  if (colgroup) {
    const columns = Array.from(colgroup.children).filter(
      (el) => el.tagName.toLowerCase() === 'col'
    ) as HTMLTableColElement[];

    const columnWidths = parseColumnWidths(expandColsInColGroup(columns));
    if (columnWidths.length > 0) {
      return columnWidths;
    }
  }

  let row = children.find(isTableRow);
  if (!row) {
    const container = children.find((child) =>
      ['thead', 'tbody', 'tfoot'].includes(child.tagName.toLowerCase())
    );
    if (container) {
      row = Array.from(container.children).find(isTableRow);
    }
  }

  if (row) {
    const tableCells = Array.from(row.children).filter(isTableCell);
    const widths = parseColumnWidths(tableCells);
    return widths;
  }
  return [];
};

// Some integrations can send us weird tables with rows, but no
// cells. These won't appear in the UI, and will actually break
// editing. So if we detect a table with either no rows, or with rows
// but no cells, we should not parse it.
export const isTableWithoutCells = (table: HTMLTableElement) => {
  let containers = Array.from(table.children).filter((child) =>
    ['tbody', 'thead', 'tfoot'].includes(child.tagName.toLowerCase())
  );

  if (containers.length === 0) {
    containers = [table];
  }

  const rows = containers.reduce<HTMLTableRowElement[]>((acc, container) => {
    const trs = Array.from(container.children).filter(isTableRow);
    if (trs.length) acc = acc.concat(trs);
    return acc;
  }, []);

  return rows.every(
    (row) => Array.from(row.children).filter(isTableCell).length === 0
  );
};
